import React, { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Trash2, FileText, Loader2, ShieldCheck, Settings2, Lock } from "lucide-react";
import { toast } from "sonner";

const SENSITIVE = ["id_card", "passport", "id_photo", "family_proof", "legal_doc"];
const STATUSES = ["deposited", "watermark_generated", "awaiting_paper_scan", "paper_scan_received", "to_verify", "verified", "refused", "to_complete", "archived"];
const STATUS_COLOR = {
  deposited: "bg-slate-100 text-slate-600", watermark_generated: "bg-sky-100 text-sky-700",
  awaiting_paper_scan: "bg-amber-100 text-amber-700", paper_scan_received: "bg-indigo-100 text-indigo-700",
  to_verify: "bg-amber-100 text-amber-700", verified: "bg-emerald-100 text-emerald-700",
  refused: "bg-red-100 text-red-700", to_complete: "bg-orange-100 text-orange-700", archived: "bg-slate-100 text-slate-500",
};

export default function Documents() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canUpload = hasRole("direction", "administration", "coordinateur", "finance", "professeur");
  const isAdmin = hasRole("direction", "administration");
  const canOriginal = hasRole("direction", "administration");
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", student_id: "", visibility: "staff", doc_type: "other" });
  const fileRef = useRef(null);
  const [manage, setManage] = useState(null);

  const load = async () => {
    const { data } = await api.get("/documents"); setRows(data);
    if (canUpload) { try { const s = await api.get("/students"); setStudents(s.data); } catch (e) {} }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name || file.name);
    fd.append("category", form.category);
    fd.append("student_id", form.student_id);
    fd.append("visibility", form.visibility);
    fd.append("doc_type", form.doc_type);
    setBusy(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("save")); setOpen(false);
      setForm({ name: "", category: "", student_id: "", visibility: "staff", doc_type: "other" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const download = (d) => {
    if (d.regenerate_url) window.open(`${process.env.REACT_APP_BACKEND_URL}${d.regenerate_url}`, "_blank");
    else window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`, "_blank");
  };
  const downloadOriginal = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/original`, "_blank");
  const del = async (id) => { if (!window.confirm(`${t("delete")} ?`)) return; await api.delete(`/documents/${id}`); load(); };
  const fmtSize = (b) => !b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
  const isSensitive = SENSITIVE.includes(form.doc_type);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">{t("documents")}</h1>
          <p className="text-sm text-slate-500">{rows.length} {t("documents").toLowerCase()}</p>
        </div>
        {canUpload && <Button data-testid="doc-upload-btn" onClick={() => setOpen(true)} className="rounded-full gap-2"><Upload className="h-4 w-4" />{t("add")}</Button>}
      </div>

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>{t("documentType")}</TableHead>
            <TableHead>{t("student")}</TableHead><TableHead>{t("status")}</TableHead>
            <TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">{t("noData")}</TableCell></TableRow> :
              rows.map((d, i) => (
                <TableRow key={d.id} data-testid="row-documents" className={i % 2 ? "bg-slate-50/40" : ""}>
                  <TableCell>
                    <div className="flex items-center gap-2 font-medium text-slate-800">
                      {d.sensitive ? <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0" /> : <FileText className="h-4 w-4 text-primary shrink-0" />}
                      {d.name}
                    </div>
                    <div className="text-xs text-slate-400 ml-6">
                      {d.original_filename} · {fmtSize(d.size)}
                      {d.document_code && <span className="ml-2 text-amber-600">{d.document_code}</span>}
                    </div>
                  </TableCell>
                  <TableCell><span className="text-slate-600">{t(`dt_${d.doc_type || "other"}`)}</span>{d.sensitive && <Badge className="ml-2 bg-amber-100 text-amber-700 border-0 text-[10px]">{t("sensitiveDoc")}</Badge>}</TableCell>
                  <TableCell className="text-slate-600">{d.student_name || "—"}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLOR[d.doc_status] || "bg-slate-100 text-slate-600"} border-0`}>{t(`st_${d.doc_status || "deposited"}`)}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {(d.storage_path || d.regenerate_url) && <Button data-testid="doc-download-btn" variant="ghost" size="icon" onClick={() => download(d)} title={d.sensitive ? t("watermarked") : t("pdf")}><Download className="h-4 w-4 text-primary" /></Button>}
                      {d.sensitive && canOriginal && <Button data-testid="doc-original-btn" variant="ghost" size="icon" onClick={() => downloadOriginal(d.id)} title={t("downloadOriginal")}><Lock className="h-4 w-4 text-amber-600" /></Button>}
                      {d.sensitive && isAdmin && <Button data-testid="doc-manage-btn" variant="ghost" size="icon" onClick={() => setManage({ ...d })} title={t("manage")}><Settings2 className="h-4 w-4 text-slate-500" /></Button>}
                      {canUpload && <Button data-testid="doc-delete-btn" variant="ghost" size="icon" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t("documents")}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Contrats, justificatifs et documents sécurisés par élève.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("documentType")}</label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="other">{t("dt_other")}</SelectItem>
                  {SENSITIVE.map((s) => <SelectItem key={s} value={s}>{t(`dt_${s}`)}</SelectItem>)}
                </SelectContent>
              </Select></div>
            {isSensitive && (
              <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                <span data-testid="doc-watermark-note">{t("watermarkNote")}</span>
              </div>
            )}
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">Fichier</label>
              <Input data-testid="doc-file" ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt" /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("name")}</label>
              <Input data-testid="doc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Contrat d'inscription" /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("subject")}</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Contrat, Justificatif…" /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("student")}</label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="doc-student"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("visibility")}</label>
              <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                <SelectTrigger data-testid="doc-visibility"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">staff</SelectItem><SelectItem value="public">public</SelectItem></SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="doc-save-btn" onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage (status + paper scan) dialog */}
      <ManageDialog manage={manage} setManage={setManage} onSaved={load} />
    </div>
  );
}

function ManageDialog({ manage, setManage, onSaved }) {
  const { t } = useI18n();
  const [status, setStatus] = useState("");
  const [paper, setPaper] = useState({});
  useEffect(() => {
    if (manage) {
      setStatus(manage.doc_status || "deposited");
      setPaper({
        paper_required: !!manage.paper_required, paper_received: !!manage.paper_received,
        paper_received_date: manage.paper_received_date || "", verified_by: manage.verified_by || "",
        physical_location: manage.physical_location || "", admin_comment: manage.admin_comment || "",
      });
    }
  }, [manage]);
  if (!manage) return null;

  const save = async () => {
    try {
      await api.put(`/documents/${manage.id}/paper`, paper);
      await api.put(`/documents/${manage.id}/status`, { doc_status: status });
      toast.success(t("save")); setManage(null); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-600" />{t("manage")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{manage.document_code} · {t(`dt_${manage.doc_type}`)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
          <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("updateStatus")}</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="manage-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`st_${s}`)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("paperRequired")}</label>
              <Select value={paper.paper_required ? "yes" : "no"} onValueChange={(v) => setPaper({ ...paper, paper_required: v === "yes" })}>
                <SelectTrigger data-testid="manage-paper-required"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">{t("yes")}</SelectItem><SelectItem value="no">{t("no")}</SelectItem></SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("paperReceived")}</label>
              <Select value={paper.paper_received ? "yes" : "no"} onValueChange={(v) => setPaper({ ...paper, paper_received: v === "yes" })}>
                <SelectTrigger data-testid="manage-paper-received"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">{t("yes")}</SelectItem><SelectItem value="no">{t("no")}</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("receivedDate")}</label>
            <Input data-testid="manage-received-date" type="date" value={paper.paper_received_date} onChange={(e) => setPaper({ ...paper, paper_received_date: e.target.value })} /></div>
          <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("verifiedBy")}</label>
            <Input value={paper.verified_by} onChange={(e) => setPaper({ ...paper, verified_by: e.target.value })} /></div>
          <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("physicalLocation")}</label>
            <Input value={paper.physical_location} onChange={(e) => setPaper({ ...paper, physical_location: e.target.value })} placeholder="Classeur A / Étagère 3…" /></div>
          <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("adminComment")}</label>
            <Textarea value={paper.admin_comment} onChange={(e) => setPaper({ ...paper, admin_comment: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setManage(null)}>{t("cancel")}</Button>
          <Button data-testid="manage-save-btn" onClick={save}>{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
